import { ReactNode, useEffect, useId, useRef, useState } from 'react';

/** Accessible weekly chart shell: one focusable column per week with a tooltip. */
export default function WeeklyChart({ labels, heading, children, compact = false }: { labels: string[]; heading: string; children: (active: number | null) => ReactNode; compact?: boolean }) {
  const [active, setActive] = useState<number | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const tooltipId = useId();
  useEffect(() => {
    const dismiss = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setActive(null); };
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, []);
  const index = active != null && active < labels.length ? active : null;
  return <div ref={root} className={`relative ${compact ? 'mt-2 h-12' : 'mt-4 h-20'}`} onPointerLeave={event => { if (event.pointerType !== 'touch') setActive(null); }} onKeyDown={event => { if (event.key === 'Escape') { setActive(null); event.stopPropagation(); } }}>
    <div aria-hidden="true" className="h-full">{children(index)}</div>
    <div className="absolute inset-0 flex" role="group" aria-label={heading}>
      {labels.map((label, i) => <button key={i} type="button" aria-label={label} aria-describedby={index === i ? tooltipId : undefined}
        className="h-full min-w-0 flex-1 cursor-crosshair rounded-sm bg-transparent focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
        onPointerEnter={() => setActive(i)} onPointerMove={() => setActive(i)} onFocus={() => setActive(i)} onBlur={() => setActive(null)} onClick={() => setActive(i)}
        onKeyDown={event => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          const next = Math.max(0, Math.min(labels.length - 1, i + (event.key === 'ArrowRight' ? 1 : -1)));
          const buttons = root.current?.querySelectorAll('button');
          buttons?.[next]?.focus();
        }} />)}
    </div>
    {index != null && <div role="tooltip" id={tooltipId} className="pointer-events-none absolute bottom-full z-20 mb-2 w-48 max-w-full rounded-lg bg-slate-900 px-3 py-2 text-xs leading-relaxed text-white shadow-md"
      style={{left: `clamp(0px, calc(${(index + .5) / labels.length * 100}% - 6rem), max(0px, calc(100% - 12rem)))`}}>{labels[index]}</div>}
  </div>;
}
